import React, { useCallback, useState } from 'react';
import type { FC } from 'react';
import Modal from 'src/common/components/Modal';
import { type DateFormValues } from 'src/inbox/components/cards/Dates/form';
import { Field, useForm, useFormState } from 'react-final-form';
import moment from 'moment';
import InputWrapper from 'src/common/components/InputWrapper';
import { MEDIA_QUERIES, MOMENT_FORMAT } from 'src/common/constants';
import Icon from 'src/common/components/IconDS22';
import { DayPicker } from 'react-day-picker';
import { formatDate, newTZDate, todayDate } from 'src/common/utils/dateHelpers';
import DayContentWithMotivators from 'src/common/components/DayPicker/DayContentWithMotivators';
import type { DateString } from 'src/common/types/UseBookingMotivators';
import useBookingMotivatorsContext from 'src/common/hooks/useBookingMotivators';
import NotificationMessage from 'src/common/components/NotificationMessage';
import BookingMotivators from 'src/common/components/BookingMotivators';
import FormButtons from 'src/common/components/FormButtons';
import { isAfter } from 'date-fns';
import CUSTOM_DATES_INDEX from 'src/inbox/components/cards/Dates/constants';
import useMediaQuery from 'src/common/hooks/useMediaQuery';
import s from './DatesFormDatePicker.module.scss';

type DatesFormDatePickerProps = {
  placeholder: string;
  fieldName: keyof Pick<DateFormValues, 'preferred_date' | 'end_date'>;
  withBookingMotivators?: boolean;
};

const DatesFormDatePicker: FC<DatesFormDatePickerProps> = ({
  placeholder,
  fieldName,
  withBookingMotivators = false,
}) => {
  const { change, batch, restart } = useForm();
  const { values } = useFormState<DateFormValues>();

  const {
    bookingMotivators, blockedTimes,
    loadBlockedTimes, handleMonthChange, someUnavailableInRange,
  } = useBookingMotivatorsContext();

  const [motivatorDetails, setMotivatorDetails] = useState<DateString>();

  const isDesktop = useMediaQuery(MEDIA_QUERIES.DESKTOP);

  const someDatesUnavailableInRange = Boolean(
    values.preferred_date && values.end_date
    && someUnavailableInRange(newTZDate(values.preferred_date), newTZDate(values.end_date)),
  );

  const [open, setOpen] = useState(false);
  const closeModal = useCallback(() => {
    setOpen(false);
    if (someDatesUnavailableInRange) {
      batch(() => {
        change('preferred_date', undefined);
        change('end_date', undefined);
      });
    }
  }, [batch, change, someDatesUnavailableInRange]);

  return (
    <>
      <Field<string> name={fieldName}>
        {({ input: { value } }) => (
          <InputWrapper name={fieldName}>
            <div className={s.root}>
              <input
                value={value ? moment(values[fieldName], MOMENT_FORMAT.VALUE_AND_TIME_24).format('MM/DD/YYYY') : ''}
                readOnly
                placeholder={placeholder}
                onClick={() => {
                  setOpen(true);
                  change('suggested_date_index', CUSTOM_DATES_INDEX);
                }}
                className={s.input}
                name={fieldName}
                aria-label={placeholder}
              />
              {value && (
                <button
                  type="button"
                  className={s.clearInput}
                  aria-label="Clear start and end dates"
                  onClick={() => (withBookingMotivators ? restart() : change(fieldName, undefined)
                  )}
                >
                  <Icon id="x" />
                </button>
              )}
            </div>
          </InputWrapper>
        )}
      </Field>
      <Modal
        modalOpen={open}
        closeModal={closeModal}
        classNameModifier="datesForm"
        label="Select trip dates"
        name="Select trip dates"
      >
        {!withBookingMotivators ? (
          <DayPicker
            mode="range"
            selected={{
              from: values.preferred_date ? newTZDate(values.preferred_date) : undefined,
              to: values.end_date ? newTZDate(values.end_date) : undefined,
            }}
            disabled={{ before: todayDate() }}
            numberOfMonths={2}
            onDayClick={(day) => {
              change('suggested_date_index', CUSTOM_DATES_INDEX);
              change(fieldName, formatDate(day));
              closeModal();
            }}
            fixedWeeks
            components={{ DayContent: DayContentWithMotivators }}
          />
        ) : (
          <>
            <DayPicker
              mode="range"
              selected={{
                from: values.preferred_date ? newTZDate(values.preferred_date) : undefined,
                to: values.end_date ? newTZDate(values.end_date) : undefined,
              }}
              disabled={{ before: todayDate() }}
              numberOfMonths={isDesktop ? 2 : 1}
              onMonthChange={handleMonthChange} // get next month motivators
              onDayClick={(day) => {
                change('suggested_date_index', CUSTOM_DATES_INDEX);
                if (values.preferred_date && values.preferred_date === values.end_date
                && isAfter(day, newTZDate(values.preferred_date))) {
                  change('end_date', formatDate(day));
                } else {
                  change('preferred_date', formatDate(day));
                  change('end_date', formatDate(day));
                }
                setMotivatorDetails(formatDate(day));
                loadBlockedTimes(day);
              }}
              fixedWeeks
              components={{ DayContent: DayContentWithMotivators }}
            />
            {someDatesUnavailableInRange && (
              <NotificationMessage outdent={false}>
                Some dates are unavailable within your selected range.
              </NotificationMessage>
            )}
            <div className={s.motivators}>
              <BookingMotivators
                selectedDate={motivatorDetails}
                blockedTimes={blockedTimes}
                selectedMotivators={
                  motivatorDetails ? bookingMotivators[motivatorDetails] : undefined
                }
              />
            </div>
            <FormButtons
              classNameModifier="inbox"
              closeModal={closeModal}
              onSubmit={closeModal}
              disabled={someDatesUnavailableInRange}
              cancelText="Back"
            />
          </>
        )}
      </Modal>
    </>
  );
};

export default DatesFormDatePicker;
